/**
 * M5T Primary Navigation Web Component
 * Sticky navigation bar with logo and stateful buttons
 *
 * Shared component - can be used across multiple client apps
 *
 * Supports hierarchical navigation:
 * - Primary navigation items (always visible)
 * - Secondary navigation items (shown when primary is selected)
 *
 * Attributes:
 * - data-buttons: JSON array of button configurations (hierarchical structure)
 * - data-logo-topic: PubSub topic to publish when logo is clicked (optional)
 * - data-show-secondary: Boolean - show secondary navigation (default: true)
 *
 * Button Schema:
 * - token: string - Unique identifier
 * - caption: string - Display text
 * - enabled: boolean - Whether button is active
 * - icon: string - CSS class for icon (e.g., "fas fa-home")
 * - admin_role: boolean - Requires admin privileges
 * - tagline: string - Tooltip/description
 * - secondary: array - Child navigation items (same schema)
 * - count: number - Optional badge count
 *
 * i18n Support:
 * - Listens for 'language-changed' event from I18nController
 * - Automatically re-renders navigation with new language
 * - Requires NavMetaManager to be accessible for dynamic updates
 *
 * Note: If data-logo-topic is used, window.m5t.pubsub must be initialized by the consuming app.
 * Example: window.m5t = { pubsub: PubSub };
 */

import { NavMetaManager } from '../meta/meta_nav.js';

class M5TPrimaryNav extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.buttons = [];
    this.selectedPrimaryToken = null;
    this.selectedSecondaryToken = null;
    this.logoTopic = null;
    this.showSecondary = true;

    // Long-tap tracking
    this.longTapTimer = null;
    this.longTapDelay = 4600; // ms (4.6 seconds)
    this.longTapActive = false;
    this.longTapFired = false; // Flag to prevent click after long-tap
  }

  connectedCallback() {
    this.logoTopic = this.getAttribute('data-logo-topic');
    this.showSecondary = this.getAttribute('data-show-secondary') !== 'false';
    this.parseButtons();
    this.render();

    // Listen for language change events to refresh navigation
    this.languageChangeHandler = () => {
      console.log('[M5TPrimaryNav] Language changed, refreshing navigation with i18n');

      // Re-fetch navigation data with new language from NavMetaManager
      const updatedNav = NavMetaManager.getPrimaryNav();

      if (updatedNav && updatedNav.length > 0) {
        // Update the data-buttons attribute with new i18n-resolved data
        this.setAttribute('data-buttons', JSON.stringify(updatedNav));

        // Re-parse, re-render, and re-attach event listeners
        this.parseButtons();
        this.render();
        this.attachEventListeners();
      } else {
        console.warn('[M5TPrimaryNav] No navigation data returned from NavMetaManager');
      }
    };
    document.addEventListener('language-changed', this.languageChangeHandler);
    this.attachEventListeners();
  }

  /**
   * Clean up event listeners when component is removed
   */
  disconnectedCallback() {
    if (this.languageChangeHandler) {
      document.removeEventListener('language-changed', this.languageChangeHandler);
    }
  }

  /**
   * Parse button data from attribute
   */
  parseButtons() {
    const buttonsData = this.getAttribute('data-buttons');
    if (buttonsData) {
      try {
        this.buttons = JSON.parse(buttonsData);
        // Find initially selected button
        const selected = this.buttons.find(btn => btn.selected);
        this.selectedPrimaryToken = selected ? selected.token : (this.buttons[0]?.token || null);

        // Secondary buttons are not stateful - no initial selection
      } catch (error) {
        console.error('[M5TPrimaryNav] Error parsing buttons:', error);
        this.buttons = [];
      }
    }
  }

  /**
   * Get secondary buttons for currently selected primary
   */
  getSecondaryButtons() {
    if (!this.selectedPrimaryToken || !this.showSecondary) return [];
    const primaryBtn = this.buttons.find(btn => btn.token === this.selectedPrimaryToken);
    return primaryBtn?.secondary || [];
  }

  /**
   * Get button data (caption, tagline) by token
   * @param {string} token - Button token
   * @param {string} level - 'primary' or 'secondary'
   * @returns {Object} Button data with caption and tagline
   */
  getButtonData(token, level) {
    if (level === 'primary') {
      const btn = this.buttons.find(b => b.token === token);
      return {
        caption: btn?.caption || '',
        tagline: btn?.tagline || ''
      };
    } else {
      // Search in secondary buttons
      for (const primaryBtn of this.buttons) {
        if (primaryBtn.secondary) {
          const secBtn = primaryBtn.secondary.find(s => s.token === token);
          if (secBtn) {
            return {
              caption: secBtn.caption || '',
              tagline: secBtn.tagline || ''
            };
          }
        }
      }
    }
    return { caption: '', tagline: '' };
  }

  /**
   * Render the component
   */
  render() {
    const secondaryButtons = this.getSecondaryButtons();
    const hasSecondary = secondaryButtons.length > 0;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          position: sticky;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          width: 100vw;
          margin: 0;
        }

        .nav-wrapper {
          background: white;
          border: 2px solid #000000;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .nav-container {
          display: grid;
          grid-template-areas: "logo ${this.buttons.map((_, i) => `btn${i}`).join(' ')}";
          grid-template-columns: 84px repeat(${this.buttons.length}, 1fr);
          height: 64px;
          background: white;
          overflow: hidden;
        }

        .secondary-nav {
          display: grid;
          grid-template-columns: repeat(${secondaryButtons.length || 1}, 1fr);
          height: 48px;
          min-height: 48px;
          background: #2d2d2d;
          border-top: 2px solid #000000;
          overflow: hidden;
        }

        .logo {
          grid-area: logo;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: var(--font-display, 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif);
          font-size: 1.5rem;
          font-weight: 700;
          color: #000000;
          border-right: 2px solid #000000;
          cursor: pointer;
          user-select: none;
          transition: background 0.3s ease;
        }

        .logo:hover {
          background: rgba(0, 0, 0, 0.05);
        }

        .nav-button {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 3px;
          background: white;
          color: #333;
          border: none;
          border-bottom: 3px solid transparent;
          border-left: 1px solid #e0e0e0;
          font-family: 'Roboto Slab', 'Helvetica Neue', Helvetica, Arial, sans-serif;
          font-size: 0.85rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          cursor: pointer;
          transition: all 0.3s ease;
          outline: none;
          padding: 6px;
        }

        .nav-button:first-of-type {
          border-left: none;
        }

        .nav-button:hover:not(.selected):not(:disabled) {
          background: #f5f5f5;
          color: #000000;
        }

        .nav-button.selected {
          background: #fafafa;
          color: #000000;
          border-bottom: 4px solid #000000;
        }

        .nav-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Icon styles */
        .nav-button-icon {
          font-size: 1rem;
          opacity: 0.8;
        }

        .nav-button.selected .nav-button-icon {
          opacity: 1;
        }

        /* Secondary navigation styles */
        .secondary-nav .nav-button {
          background: #2d2d2d;
          color: #ffffff;
          font-size: 0.75rem;
          font-weight: 500;
          border-bottom: none;
          border-left: 1px solid #555;
          height: 48px;
          gap: 2px;
        }

        .secondary-nav .nav-button:first-of-type {
          border-left: none;
        }

        .secondary-nav .nav-button:hover:not(:disabled) {
          background: #3a3a3a;
          color: #ffffff;
        }

        .secondary-nav .nav-button-icon {
          font-size: 0.85rem;
          opacity: 0.9;
        }

        .secondary-nav .nav-button:hover:not(:disabled) .nav-button-icon {
          opacity: 1;
        }

        /* Count badge */
        .count-badge {
          position: absolute;
          top: 8px;
          right: 12px;
          min-width: 20px;
          height: 20px;
          padding: 0 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #000000;
          color: white;
          font-size: 11px;
          font-weight: 700;
          border-radius: 10px;
          line-height: 1;
        }

        /* Admin role indicator */
        .nav-button[data-admin="true"]::before {
          content: '';
          position: absolute;
          top: 4px;
          left: 4px;
          width: 8px;
          height: 8px;
          background: #dc3545;
          border-radius: 50%;
          box-shadow: 0 0 4px rgba(220, 53, 69, 0.5);
        }

        /* Tooltip for tagline */
        .nav-button[title]:hover::after {
          content: attr(title);
          position: absolute;
          bottom: -40px;
          left: 50%;
          transform: translateX(-50%);
          padding: 8px 12px;
          background: rgba(0, 0, 0, 0.9);
          color: white;
          font-size: 0.75rem;
          font-weight: 400;
          text-transform: none;
          letter-spacing: normal;
          white-space: nowrap;
          border-radius: 4px;
          pointer-events: none;
          z-index: 1001;
          opacity: 0;
          animation: tooltipFadeIn 0.2s ease-in forwards;
        }

        @keyframes tooltipFadeIn {
          to { opacity: 1; }
        }

        @media (max-width: 768px) {
          :host {
            width: 100vw;
          }

          .nav-container {
            height: 54px;
            grid-template-columns: 64px repeat(${this.buttons.length}, 1fr);
          }

          .secondary-nav {
            height: 42px;
            min-height: 42px;
            grid-template-columns: repeat(${secondaryButtons.length || 1}, 1fr);
          }

          .logo {
            font-size: 1.1rem;
          }

          .nav-button {
            font-size: 0.75rem;
            gap: 2px;
          }

          .nav-button-icon {
            font-size: 0.9rem;
          }

          .secondary-nav .nav-button {
            font-size: 0.7rem;
          }

          .secondary-nav .nav-button-icon {
            font-size: 0.8rem;
          }

          .count-badge {
            top: 4px;
            right: 4px;
            min-width: 18px;
            height: 18px;
            font-size: 10px;
          }

          /* Hide tooltips on mobile */
          .nav-button[title]:hover::after {
            display: none;
          }
        }
      </style>

      <div class="nav-wrapper">
        <nav class="nav-container">
          <div class="logo">SSE</div>
          ${this.buttons.map((btn, index) => this.renderButton(btn, index, 'primary')).join('')}
        </nav>
        <nav class="secondary-nav">
          ${hasSecondary ? secondaryButtons.map((btn, index) => this.renderButton(btn, index, 'secondary')).join('') : ''}
        </nav>
      </div>
    `;
  }

  /**
   * Render a single button
   */
  renderButton(btn, index, level = 'primary') {
    // Only primary buttons are stateful (can be selected)
    // Secondary buttons are not stateful
    const isSelected = level === 'primary'
      ? btn.token === this.selectedPrimaryToken
      : false;

    const hasCount = typeof btn.count === 'number' && btn.count !== null && btn.count !== undefined;
    const hasIcon = btn.icon && btn.icon.trim() !== '';
    const hasTagline = btn.tagline && btn.tagline.trim() !== '';

    return `
      <button
        class="nav-button ${isSelected ? 'selected' : ''}"
        data-token="${btn.token}"
        data-level="${level}"
        data-admin="${btn.admin_role || false}"
        ${!btn.enabled ? 'disabled' : ''}
        ${hasTagline ? `title="${btn.tagline}"` : ''}
        style="grid-area: ${level === 'primary' ? `btn${index}` : 'auto'}"
      >
        ${hasIcon ? `<i class="${btn.icon} nav-button-icon"></i>` : ''}
        <span>${btn.caption}</span>
        ${hasCount ? `<span class="count-badge">${btn.count}</span>` : ''}
      </button>
    `;
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    const buttons = this.shadowRoot.querySelectorAll('.nav-button');
    buttons.forEach(button => {
      const token = button.getAttribute('data-token');
      const level = button.getAttribute('data-level');

      // Click event (but not if long-tap just fired)
      button.addEventListener('click', (e) => {
        if (token && !e.currentTarget.disabled && !this.longTapFired) {
          if (level === 'primary') {
            this.selectPrimaryButton(token);
          } else {
            this.selectSecondaryButton(token);
          }
        }
        // Reset long-tap flag
        this.longTapFired = false;
      });

      // Double-click event
      button.addEventListener('dblclick', (e) => {
        if (token && !e.currentTarget.disabled) {
          const buttonData = this.getButtonData(token, level);
          console.log('[M5TPrimaryNav] Button double-clicked:', token);
          this.dispatchEvent(new CustomEvent('nav-double-tap', {
            bubbles: true,
            composed: true,
            detail: {
              pubsubTopic: 'M5T_PRIMARY_NAV.DOUBLE_TAP',
              token,
              level,
              caption: buttonData.caption,
              tagline: buttonData.tagline,
              timestamp: Date.now()
            }
          }));
        }
      });

      // Hover events
      button.addEventListener('mouseenter', (e) => {
        if (token && !e.currentTarget.disabled) {
          const buttonData = this.getButtonData(token, level);
          this.dispatchEvent(new CustomEvent('nav-hover', {
            bubbles: true,
            composed: true,
            detail: {
              pubsubTopic: 'M5T_PRIMARY_NAV.HOVER',
              token,
              level,
              caption: buttonData.caption,
              tagline: buttonData.tagline,
              action: 'enter',
              timestamp: Date.now()
            }
          }));
        }
      });

      button.addEventListener('mouseleave', (e) => {
        if (token && !e.currentTarget.disabled) {
          const buttonData = this.getButtonData(token, level);
          this.dispatchEvent(new CustomEvent('nav-hover', {
            bubbles: true,
            composed: true,
            detail: {
              pubsubTopic: 'M5T_PRIMARY_NAV.HOVER',
              token,
              level,
              caption: buttonData.caption,
              tagline: buttonData.tagline,
              action: 'leave',
              timestamp: Date.now()
            }
          }));
        }
      });

      // Long-tap events (mousedown held for 4.6 seconds)
      button.addEventListener('mousedown', (e) => {
        if (token && !e.currentTarget.disabled) {
          this.longTapActive = true;
          this.longTapFired = false;

          // Start timer
          this.longTapTimer = setTimeout(() => {
            const buttonData = this.getButtonData(token, level);
            console.log('[M5TPrimaryNav] Button long-tap:', token);

            // Set flag to prevent click from firing
            this.longTapFired = true;

            this.dispatchEvent(new CustomEvent('nav-long-tap', {
              bubbles: true,
              composed: true,
              detail: {
                pubsubTopic: 'M5T_PRIMARY_NAV.LONG_TAP',
                token,
                level,
                caption: buttonData.caption,
                tagline: buttonData.tagline,
                timestamp: Date.now()
              }
            }));
          }, this.longTapDelay);
        }
      });

      button.addEventListener('mouseup', (e) => {
        // Cancel long-tap timer on mouseup
        if (this.longTapTimer) {
          clearTimeout(this.longTapTimer);
          this.longTapTimer = null;
        }
        this.longTapActive = false;
      });

      // Also cancel on mouseleave (if user drags off button)
      button.addEventListener('mouseleave', (e) => {
        if (this.longTapTimer) {
          clearTimeout(this.longTapTimer);
          this.longTapTimer = null;
        }
        this.longTapActive = false;
      });
    });

    // Logo click event
    const logo = this.shadowRoot.querySelector('.logo');
    if (logo) {
      logo.addEventListener('click', () => {
        console.log('[M5TPrimaryNav] Logo clicked');

        // Publish to PubSub if topic is configured and m5t.pubsub is available
        if (this.logoTopic && window.m5t?.pubsub) {
          console.log(`[M5TPrimaryNav] Publishing to topic: ${this.logoTopic}`);
          window.m5t.pubsub.publish(this.logoTopic, {
            timestamp: Date.now()
          });
        } else if (this.logoTopic && !window.m5t?.pubsub) {
          console.warn('[M5TPrimaryNav] Logo topic configured but window.m5t.pubsub not found');
        }

        // Dispatch custom event
        this.dispatchEvent(new CustomEvent('logo-click', {
          bubbles: true,
          composed: true,
          detail: {
            pubsubTopic: 'M5T_PRIMARY_NAV.LOGO_TAP',
            timestamp: Date.now()
          }
        }));
      });
    }
  }

  /**
   * Select a primary button by token
   */
  selectPrimaryButton(token) {
    const primaryBtn = this.buttons.find(btn => btn.token === token);
    const buttonData = this.getButtonData(token, 'primary');

    // Dispatch custom event before selection changes
    this.dispatchEvent(new CustomEvent('nav-select', {
      bubbles: true,
      composed: true,
      detail: {
        pubsubTopic: 'M5T_PRIMARY_NAV.TAP',
        token,
        level: 'primary',
        caption: buttonData.caption,
        tagline: buttonData.tagline,
        hasSecondary: (primaryBtn?.secondary?.length || 0) > 0,
        timestamp: Date.now()
      }
    }));

    // Only re-render if selection changed
    if (this.selectedPrimaryToken !== token) {
      this.selectedPrimaryToken = token;

      // No auto-selection of secondary buttons (they are not stateful)

      this.render();
      this.attachEventListeners();
    }
  }

  /**
   * Select a secondary button by token
   * Secondary buttons are not stateful - they don't maintain selected state
   */
  selectSecondaryButton(token) {
    const buttonData = this.getButtonData(token, 'secondary');

    // Dispatch custom event (no state change, just event)
    this.dispatchEvent(new CustomEvent('nav-select', {
      bubbles: true,
      composed: true,
      detail: {
        pubsubTopic: 'M5T_PRIMARY_NAV.TAP',
        token,
        primaryToken: this.selectedPrimaryToken,
        level: 'secondary',
        caption: buttonData.caption,
        tagline: buttonData.tagline,
        timestamp: Date.now()
      }
    }));

    // No state change or re-render for secondary buttons
  }

  /**
   * Public method to update buttons
   */
  updateButtons(buttonsData) {
    this.setAttribute('data-buttons', JSON.stringify(buttonsData));
    this.parseButtons();
    this.render();
    this.attachEventListeners();
  }

  /**
   * Public method to update a specific button's count
   */
  updateCount(token, count, level = 'primary') {
    let button;

    if (level === 'primary') {
      button = this.buttons.find(btn => btn.token === token);
    } else {
      const secondaryButtons = this.getSecondaryButtons();
      button = secondaryButtons.find(btn => btn.token === token);
    }

    if (button) {
      button.count = count;
      this.render();
      this.attachEventListeners();
    }
  }

  /**
   * Public method to get currently selected tokens
   * Note: Secondary buttons are not stateful, so secondary is always null
   */
  getSelection() {
    return {
      primary: this.selectedPrimaryToken,
      secondary: null  // Secondary buttons are not stateful
    };
  }
}

// Register the custom element
customElements.define('m5t-primary-nav', M5TPrimaryNav);

export default M5TPrimaryNav;
